import { useEffect, useState } from "react";
import { Gift, CheckCircle2, X } from "lucide-react";
import { api } from "@/lib/api";

const STATES = ["MD", "DC", "VA"];

export default function StepDetails({ form, setForm, referralCode, setReferralCode }) {
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const [refStatus, setRefStatus] = useState(null); // null | 'checking' | { valid, credit, name }
  const [showReferral, setShowReferral] = useState(!!referralCode);

  useEffect(() => {
    const code = (referralCode || "").trim().toUpperCase();
    if (!code || code.length < 4) {
      setRefStatus(null);
      return;
    }
    setRefStatus({ status: "checking" });
    const t = setTimeout(() => {
      api.get(`/referrals/validate?code=${encodeURIComponent(code)}`)
        .then((r) => {
          if (r.data?.valid) {
            setRefStatus({ status: "valid", credit: r.data.credit, name: r.data.referrer_first_name });
          } else {
            setRefStatus({ status: "invalid" });
          }
        })
        .catch(() => setRefStatus({ status: "invalid" }));
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode]);

  return (
    <div data-testid="step-details">
      <h2 className="font-heading text-2xl tracking-tight mb-6">Your details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input className="input-base" placeholder="Full name" value={form.name} onChange={set("name")} data-testid="form-name" />
        <input className="input-base" type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="form-email" />
        <input className="input-base" placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="form-phone" />
        <input className="input-base" placeholder="ZIP (optional)" value={form.zip_code} onChange={set("zip_code")} data-testid="form-zip" />
        <input className="input-base md:col-span-2" placeholder="Street address" value={form.address} onChange={set("address")} data-testid="form-address" />
        <input className="input-base" placeholder="City" value={form.city} onChange={set("city")} data-testid="form-city" />
        <select className="input-base" value={form.state} onChange={set("state")} data-testid="form-state">
          {STATES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <textarea
          className="input-base md:col-span-2 min-h-[120px]"
          placeholder="Notes (vent count, pets, gate codes, etc.)"
          value={form.notes}
          onChange={set("notes")}
          data-testid="form-notes"
        />
      </div>

      {/* Referral code */}
      <div className="mt-6">
        {!showReferral ? (
          <button
            type="button"
            onClick={() => setShowReferral(true)}
            className="inline-flex items-center gap-2 text-sm text-forest hover:underline"
            data-testid="show-referral"
          >
            <Gift size={14} /> Have a referral code?
          </button>
        ) : (
          <div className="card-surface p-5" data-testid="referral-section">
            <div className="flex items-center gap-2 mb-3">
              <Gift size={16} className="text-clay" />
              <span className="eyebrow">Referral code</span>
              <button
                type="button"
                onClick={() => { setShowReferral(false); setReferralCode(""); setRefStatus(null); }}
                className="ml-auto text-muted2 hover:text-ink"
                aria-label="Remove referral"
              >
                <X size={14} />
              </button>
            </div>
            <input
              className="input-base uppercase tracking-widest"
              placeholder="ENTER CODE"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              maxLength={12}
              data-testid="referral-input"
            />
            {refStatus?.status === "checking" && (
              <div className="text-xs text-muted2 mt-2">Checking…</div>
            )}
            {refStatus?.status === "valid" && (
              <div className="text-sm text-forest mt-3 flex items-center gap-2" data-testid="referral-valid">
                <CheckCircle2 size={14} /> Nice — ${refStatus.credit} credit unlocked from {refStatus.name}.
              </div>
            )}
            {refStatus?.status === "invalid" && referralCode.length >= 4 && (
              <div className="text-sm text-red-700 mt-3" data-testid="referral-invalid">
                That code isn't valid. Double-check with your friend.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
